import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DataExplorer from '../data-explorer/DataExplorer'
import UploadFileStep, { UploadSelection } from './UploadFileStep'
import { GenNode } from '../../types/carbon/models/GenNode'
import { ThemeAIDataSource, ThemeAITask, ThemeAIUploadInspection } from '../../types/gotypes'
import { suggestedReuseName } from './reuseSetup'
import { THEMEAI_FILE_UPLOAD } from '../../utils/themeAIFeatures'

type EntryPoint = 'job_variable' | 'upload'

interface NewTaskDialogProps {
  open: boolean
  customer: string
  job: string
  creating: boolean
  onClose: () => void
  /** Called once the dialog has finished closing, so the page can clear
   *  state the dialog is keyed on without cutting the close transition. */
  onExited?: () => void
  onInspectUpload: (file: File) => Promise<ThemeAIUploadInspection>
  onCreate: (name: string, dataSource: ThemeAIDataSource, upload?: UploadSelection) => void | Promise<void>
  /** Set when the user reuses a task's set-up on other data (#1492 item 9).
   *  The page adds the set-up to the create call; the dialog only says what
   *  carries over and asks for the new data. Remount (key) to change it. */
  reuseFrom?: ThemeAITask
}

// One plain sentence naming what carries over, in the order the steps use it.
const reuseSummary = (task: ThemeAITask): string => {
  const parts = [
    task.code_frame_id ? `the code frame${task.code_frame_version ? ` (version ${task.code_frame_version})` : ''}` : '',
    task.study_context?.trim() ? 'the task context' : '',
  ].filter(Boolean)
  return parts.length ? `It keeps ${parts.join(' and ')}.` : ''
}

const NewTaskDialog: React.FC<NewTaskDialogProps> = ({ open, customer, job, creating, onClose, onExited, onInspectUpload, onCreate, reuseFrom }) => {
  const [name, setName] = useState(reuseFrom ? suggestedReuseName(reuseFrom) : '')
  const [entryPoint, setEntryPoint] = useState<EntryPoint>('job_variable')
  const [selectedNode, setSelectedNode] = useState<GenNode | undefined>()
  const [upload, setUpload] = useState<UploadSelection>()

  const reset = () => {
    setName('')
    setEntryPoint('job_variable')
    setSelectedNode(undefined)
    setUpload(undefined)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleUploadChange = (selection: UploadSelection | undefined) => {
    setUpload(selection)
    if (selection && !name.trim()) setName(selection.file.name.replace(/\.(csv|xlsx)$/i, ''))
  }

  const canCreate = name.trim().length > 0 && (
    (entryPoint === 'job_variable' && !!selectedNode)
    || (entryPoint === 'upload' && !!upload?.columns.textColumn)
  )

  const handleCreate = () => {
    if (!canCreate) return
    if (entryPoint === 'job_variable' && selectedNode) {
      const dataSource: ThemeAIDataSource = {
        type: 'job_variable',
        job_variable: {
          job,
          variable_name: selectedNode.value1 || '',
          question: selectedNode.value2 || '',
        },
      }
      onCreate(name.trim(), dataSource)
    } else if (entryPoint === 'upload' && upload) {
      const dataSource: ThemeAIDataSource = {
        type: 'upload',
        upload: { file_name: upload.file.name },
      }
      onCreate(name.trim(), dataSource, upload)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} TransitionProps={{ onExited }} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {reuseFrom ? 'Use this set-up on other data' : 'New Theme AI Task'}
        <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {reuseFrom && (
          <Alert severity="info" icon={false}>
            This makes a new task from the set-up of “{reuseFrom.name}”.
            {' '}{reuseSummary(reuseFrom)}
            {' '}Choose the data to code. The original task does not change.
          </Alert>
        )}
        <TextField
          label="Task name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          autoFocus
          size="small"
          placeholder="e.g. Q3 Satisfaction — open comments"
        />

        {THEMEAI_FILE_UPLOAD && (
          <ToggleButtonGroup
            value={entryPoint}
            exclusive
            size="small"
            onChange={(_, value) => value && setEntryPoint(value)}
          >
            <ToggleButton value="job_variable">Use a job variable</ToggleButton>
            <ToggleButton value="upload">Upload a CSV or Excel file</ToggleButton>
          </ToggleButtonGroup>
        )}

        {entryPoint === 'job_variable' ? (
          <Box sx={{ height: 420, border: '1px solid', borderColor: 'divider' }}>
            <DataExplorer
              customer={customer}
              job={job}
              onVariableChange={setSelectedNode}
              onDataChange={() => { /* preview only — full data loads once the task exists */ }}
            />
          </Box>
        ) : (
          <UploadFileStep onInspect={onInspectUpload} onChange={handleUploadChange} disabled={creating} />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">Cancel</Button>
        <Button variant="contained" color="info" disabled={!canCreate || creating} onClick={handleCreate}>
          {creating ? 'Saving task…' : 'Create Task'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NewTaskDialog
